import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bookmark, Trash2, FileSpreadsheet, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { FILTER_LABELS } from '@/lib/buyersV2';
import { ImportSummary } from './ImportWizard';

export const filterText = (filters) => Object.entries(filters || {}).filter(([k]) => !['sort_by', 'sort_order'].includes(k))
  .map(([k, v]) => `${FILTER_LABELS[k] || k}: ${v === 'true' ? 'yes' : v}`).join(' · ') || 'All buyers';

export function SaveSegmentDialog({ open, onOpenChange, filters, onSaved }) {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(''); }, [open]);
  const save = async () => {
    if (!name.trim()) { toast.error('Name the segment'); return; }
    try { await API.post('/buyer-segments', { name, filters }); toast.success('Segment saved'); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="save-segment-dialog">
        <DialogHeader><DialogTitle>Save as Segment</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">{filterText(filters)}</p>
        <Input data-testid="segment-name-input" value={name} onChange={e => setName(e.target.value)} placeholder='e.g. Hot SFR buyers 33157' className="bg-white border-gray-300" onKeyDown={e => e.key === 'Enter' && save()} />
        <Button data-testid="segment-save-btn" onClick={save} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">Save Segment</Button>
      </DialogContent>
    </Dialog>
  );
}

export function SegmentsTab({ onOpenSegment }) {
  const [items, setItems] = useState(null);
  const load = () => API.get('/buyer-segments').then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const remove = async (s) => { if (!window.confirm(`Delete segment "${s.name}"?`)) return; await API.delete(`/buyer-segments/${s.id}`); load(); };
  if (items === null) return <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />;
  if (!items.length) return <p data-testid="no-segments" className="text-sm text-gray-400 text-center py-14">No saved segments yet. Filter the buyer list and click "Save Segment".</p>;
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="segments-tab">
      {items.map(s => (
        <div key={s.id} data-testid={`segment-${s.id}`} className="border border-gray-200 rounded-xl bg-white p-4 flex items-start gap-3 hover:border-[#0073ea]/40 transition-colors">
          <Bookmark className="h-5 w-5 text-[#0073ea] mt-0.5" />
          <button className="flex-1 text-left min-w-0" onClick={() => onOpenSegment(s)} data-testid={`open-segment-${s.id}`}>
            <p className="font-semibold text-gray-900">{s.name} <span className="text-sm text-gray-400 font-normal">· {s.count} buyers</span></p>
            <p className="text-xs text-gray-500 truncate">{filterText(s.filters)}</p>
          </button>
          <button onClick={() => remove(s)} className="text-gray-300 hover:text-red-500" data-testid={`delete-segment-${s.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

function JobRow({ j }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white" data-testid={`import-job-${j.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
        <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-gray-900 truncate">{j.filename}</p><p className="text-[11px] text-gray-500">{new Date(j.created_at).toLocaleString()} · {j.status}</p></div>
        <span className="text-xs text-gray-600">{j.created} new · {j.updated} updated{j.error_count ? <span className="text-red-600"> · {j.error_count} errors</span> : ''}</span>
      </button>
      {open && <div className="px-4 pb-4"><ImportSummary job={j} /></div>}
    </div>
  );
}

export function ImportHistoryTab({ refreshKey }) {
  const [items, setItems] = useState(null);
  useEffect(() => { API.get('/buyers/imports').then(r => setItems(r.data.items)).catch(() => setItems([])); }, [refreshKey]);
  if (items === null) return <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />;
  if (!items.length) return <p data-testid="no-imports" className="text-sm text-gray-400 text-center py-14">No imports yet.</p>;
  return <div className="space-y-2" data-testid="import-history">{items.map(j => <JobRow key={j.id} j={j} />)}</div>;
}
